import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useCollectionBinding,
  usePropertyField,
  useSlotGroup,
  useTokens,
} from '@vibesites/runtime/react';
import { EditableCollectionBinding, EditableSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { getContrastTextColor } from '../utils/luminance';

const SLIDE_INTERVAL = 6000;

interface GalleryPhoto {
  image?: string | null;
  original_url?: string | null;
  alt_text?: string | null;
  title?: string | null;
}

const featuredImageDef: SlotGroupDefinition = {
  label: 'Fallback Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Property hero image',
    },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/floor-plans',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Find Your Space',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

const callout1Def: SlotGroupDefinition = {
  label: 'Callout 1 — Amenities',
  fields: {
    title: { label: 'Title', type: 'text', defaultValue: 'Tailored Living' },
    url: { label: 'URL', type: 'url', defaultValue: '/amenities' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Explore Our Amenities →' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const callout2Def: SlotGroupDefinition = {
  label: 'Callout 2 — Virtual Tour',
  fields: {
    title: { label: 'Title', type: 'text', defaultValue: 'Apartments For Your Lifestyle' },
    url: { label: 'URL', type: 'url', defaultValue: '/virtual-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Take a Virtual Tour →' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const callout3Def: SlotGroupDefinition = {
  label: 'Callout 3 — Neighborhood',
  fields: {
    title: { label: 'Title', type: 'text', defaultValue: 'A Vibrant Neighborhood' },
    url: { label: 'URL', type: 'url', defaultValue: '/neighborhood' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Explore the Neighborhood →' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

function photoUrl(photo: GalleryPhoto): string {
  return photo.image || photo.original_url || '';
}

function HeroSlider({ photos }: { photos: GalleryPhoto[] }) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = photos.length;

  const advance = useCallback(
    (dir: 1 | -1) => {
      setCurrent((prev) => (prev + dir + count) % count);
    },
    [count],
  );

  useEffect(() => {
    if (count <= 1 || paused) return;
    const timer = setInterval(() => advance(1), SLIDE_INTERVAL);
    return () => clearInterval(timer);
  }, [count, paused, advance]);

  if (count === 0) return null;

  return (
    <div
      className="hero-slider absolute inset-0"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-roledescription="carousel"
      aria-label="Hero image slideshow"
    >
      {photos.map((photo, i) => {
        const url = photoUrl(photo);
        if (!url) return null;
        return (
          <VibesitesImage
            key={`${url}-${i}`}
            url={url}
            alt={photo.alt_text || photo.title || ''}
            priority={i === 0}
            className={`hero-slider__slide${i === current ? ' hero-slider__slide--active' : ''}`}
            sizes="100vw"
            objectFit="cover"
          />
        );
      })}
    </div>
  );
}

export function Hero() {
  const bgImage = useSlotGroup('hero.bg_image', featuredImageDef);
  const ctaLink = useSlotGroup('hero.cta_link', ctaLinkDef);
  const callout1 = useSlotGroup('hero.callout_1', callout1Def);
  const callout2 = useSlotGroup('hero.callout_2', callout2Def);
  const callout3 = useSlotGroup('hero.callout_3', callout3Def);

  const city = usePropertyField('property_address_city');
  const state = usePropertyField('property_address_state');

  // Callout 3 uses the property's third brand color. When a property hasn't
  // defined a color 3, fall back to the primary color so it matches callout 1.
  const propColor3 = usePropertyField('property_color_3');
  const callout3PropColor =
    propColor3 && String(propColor3).trim() ? String(propColor3).trim() : null;
  const callout3Color = callout3PropColor ?? 'var(--vibe-color-primary)';

  // Each callout sits on a different brand color, so white text can wash out on
  // the lighter ones. Resolve the actual color values (property overrides flow
  // through the design tokens) and pick a light/dark text color per callout so
  // contrast stays readable regardless of how bright the background is.
  const tokens = useTokens();
  const callout1Text = getContrastTextColor(tokens?.color?.primary);
  const callout2Text = getContrastTextColor(tokens?.color?.accent);
  const callout3Text = getContrastTextColor(callout3PropColor ?? tokens?.color?.primary);

  const subtitleHtml = useMemo(() => {
    const cityStr = (city || 'North Hollywood').toUpperCase();
    const stateStr = state ? `, ${String(state).toUpperCase()}` : '';
    return `<p>AT ${cityStr}${stateStr}</p>`;
  }, [city, state]);

  const gallerySlides = useCollectionBinding<GalleryPhoto>('hero.slides', {
    collection: 'photo_gallery',
    defaultLimit: 10,
  });

  const slides = useMemo(
    () => (gallerySlides || []).filter((photo) => !!photoUrl(photo)),
    [gallerySlides],
  );

  const hasSlides = slides.length > 0;

  return (
    <section className="hero-section relative w-full min-h-[100svh] overflow-hidden flex flex-col">
      {/* Background slideshow */}
      <div className="absolute inset-0">
        <EditableCollectionBinding bindingPath="hero.slides" label="Hero Slideshow Images">
          {hasSlides ? (
            <HeroSlider photos={slides} />
          ) : (
            <EditableSlotGroup groupPath="hero.bg_image" definition={featuredImageDef}>
              {bgImage.image ? (
                <VibesitesImage
                  url={bgImage.image}
                  alt={bgImage.alt || ''}
                  priority
                  className="absolute inset-0 w-full h-full object-cover"
                  sizes="100vw"
                  objectFit="cover"
                />
              ) : (
                <div className="absolute inset-0 bg-[var(--vibe-color-text)]" />
              )}
            </EditableSlotGroup>
          )}
        </EditableCollectionBinding>
      </div>

      {/* Dark overlay for legibility */}
      <div className="absolute inset-0 bg-black/45 pointer-events-none" />

      {/*
        Title group flows above the bottom callouts and centers in the space
        left between the transparent header (reserved by pt) and the callouts
        (which sit at the bottom via mt-auto). Keeping it in normal flow at
        every breakpoint keeps the top/bottom spacing balanced instead of
        depending on a hand-tuned bottom reserve.
      */}
      <div className="relative z-10 flex flex-1 flex-col items-center justify-center text-center text-white px-6 sm:px-8 pt-[calc(var(--offer-banner-height,0px)+6.5rem)] pb-10 sm:pb-14 pointer-events-none">
        <RichTextContent
          path="hero.pre_title"
          label="Pre-Title"
          defaultValue="<p>Urban Chic Apartments</p>"
          as="p"
          className="hero-entrance hero-entrance-1 font-body text-xs sm:text-sm tracking-[0.35em] uppercase opacity-90 mb-3 drop-shadow-md pointer-events-auto"
        />
        <RichTextContent
          path="hero.title"
          label="Title"
          defaultValue='<p><span data-type="field-token" data-field="property_name"></span></p>'
          as="h1"
          className="hero-entrance hero-entrance-2 font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold mb-4 max-w-5xl leading-none tracking-tight drop-shadow-lg uppercase pointer-events-auto"
        />
        <RichTextContent
          path="hero.subtitle"
          label="Subtitle"
          defaultValue={subtitleHtml}
          as="p"
          className="hero-entrance hero-entrance-3 font-body text-lg sm:text-xl md:text-2xl max-w-5xl mb-10 tracking-[0.2em] uppercase opacity-95 drop-shadow-md pointer-events-auto"
        />

        <EditableSlotGroup groupPath="hero.cta_link" definition={ctaLinkDef}>
          <a
            href={ctaLink.url || '/floor-plans'}
            target={ctaLink._target || '_self'}
            className="hero-entrance hero-entrance-4 hero-cta relative inline-flex items-center px-10 py-3.5 text-xs sm:text-sm font-medium tracking-[0.25em] uppercase text-white overflow-hidden transition-all duration-300 pointer-events-auto"
          >
            <span className="relative z-10">{ctaLink.label || 'Find Your Space'}</span>
          </a>
        </EditableSlotGroup>
      </div>

      {/* Bottom feature callouts — gapped cards with edge inset, matching reference */}
      <div className="relative z-10 mt-auto grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 md:gap-4 w-full px-2 sm:px-4 md:px-5 pb-2 sm:pb-3 md:pb-4 pointer-events-none">
        <EditableSlotGroup groupPath="hero.callout_1" definition={callout1Def} className="h-full">
          <div
            className="hero-callout hero-callout--burgundy pointer-events-auto"
            style={{ ['--hero-callout-text' as string]: callout1Text }}
          >
            <p className="hero-callout__title">{callout1.title}</p>
            <a
              href={callout1.url || '#'}
              target={callout1._target || '_self'}
              className="hero-callout__link"
            >
              {callout1.label}
            </a>
          </div>
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="hero.callout_2" definition={callout2Def} className="h-full">
          <div
            className="hero-callout hero-callout--blue pointer-events-auto"
            style={{ ['--hero-callout-text' as string]: callout2Text }}
          >
            <p className="hero-callout__title">{callout2.title}</p>
            <a
              href={callout2.url || '#'}
              target={callout2._target || '_self'}
              className="hero-callout__link"
            >
              {callout2.label}
            </a>
          </div>
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="hero.callout_3" definition={callout3Def} className="h-full">
          <div
            className="hero-callout pointer-events-auto"
            style={{
              background: `color-mix(in srgb, ${callout3Color} 88%, transparent)`,
              ['--hero-callout-text' as string]: callout3Text,
            }}
          >
            <p className="hero-callout__title">{callout3.title}</p>
            <a
              href={callout3.url || '#'}
              target={callout3._target || '_self'}
              className="hero-callout__link"
            >
              {callout3.label}
            </a>
          </div>
        </EditableSlotGroup>
      </div>

      <style>{`
        .hero-slider {
          overflow: hidden;
        }
        .hero-slider__slide {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0;
          transition: opacity 1.2s ease-in-out;
        }
        .hero-slider__slide--active {
          opacity: 1;
        }
        @keyframes heroEntrance {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-entrance {
          animation: heroEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hero-entrance-1 { animation-delay: 0.1s; }
        .hero-entrance-2 { animation-delay: 0.3s; }
        .hero-entrance-3 { animation-delay: 0.55s; }
        .hero-entrance-4 { animation-delay: 0.8s; }
        .hero-cta {
          background: rgba(0, 0, 0, 0.25);
          border: 1.5px solid rgba(255, 255, 255, 0.85);
          border-radius: 0;
          color: #fff;
        }
        .hero-cta:hover {
          background: rgba(255, 255, 255, 0.12);
          border-color: #fff;
          transform: translateY(-2px);
        }
        .hero-cta:active {
          transform: translateY(0);
        }
        .hero-callout {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          text-align: center;
          gap: 0.45rem;
          height: 100%;
          min-height: 6.5rem;
          padding: 1rem 0.65rem;
          color: var(--hero-callout-text, #fff);
        }
        @media (min-width: 640px) {
          .hero-callout {
            gap: 0.65rem;
            min-height: 8.5rem;
            padding: 1.75rem 1.5rem;
          }
        }
        .hero-callout__title {
          font-family: var(--vibe-font-heading);
          font-size: 1.15rem;
          font-weight: 600;
          line-height: 1.2;
          margin: 0;
        }
        .hero-callout__link {
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--hero-callout-text, #fff);
          text-decoration: none;
          transition: opacity 0.2s ease;
        }
        @media (min-width: 640px) {
          .hero-callout__title {
            font-size: clamp(0.8rem, 2.4vw, 1.35rem);
          }
          .hero-callout__link {
            font-size: clamp(0.55rem, 1.5vw, 0.7rem);
          }
        }
        .hero-callout__link:hover {
          opacity: 0.85;
          text-decoration: underline;
          text-underline-offset: 4px;
        }
        .hero-callout--burgundy {
          background: color-mix(in srgb, var(--vibe-color-primary) 88%, transparent);
        }
        .hero-callout--blue {
          background: color-mix(in srgb, var(--vibe-color-accent) 75%, transparent);
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-entrance { animation: none; opacity: 1; transform: none; }
          .hero-slider__slide { transition: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero Section',
  tokens: {
    'hero.pre_title': {
      label: 'Pre-Title',
      type: 'richtext',
      defaultValue: '<p>Urban Chic Apartments</p>',
    },
    'hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_name"></span></p>',
    },
    'hero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>At North Hollywood</p>',
    },
  },
  slotGroups: {
    'hero.bg_image': featuredImageDef,
    'hero.cta_link': ctaLinkDef,
    'hero.callout_1': callout1Def,
    'hero.callout_2': callout2Def,
    'hero.callout_3': callout3Def,
  },
  collectionBindings: {
    'hero.slides': {
      label: 'Hero Slideshow Images',
      collection: 'photo_gallery',
      defaultLimit: 10,
    },
  },
};

(Hero as any).contentSchema = contentSchema;

export default Hero;
